import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

const STORAGE_KEY = "docFabPos:v1";
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
const DEFAULT_TIP_SIZE = { width: 220, height: 44 };

export default function DraggableFab({ children, defaultRight = 56, defaultBottom = 64, edgePadding = 8, onActivate, tooltip, tooltipId = "doc-fab-tip", tooltipDelay = 250, tooltipOffset = 8, tooltipEstimatedSize = DEFAULT_TIP_SIZE }) {
  const nodeRef = useRef(null);

  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [moved, setMoved] = useState(false);
  const startRef = useRef({ dx: 0, dy: 0, xAtDown: 0, yAtDown: 0 });
  const [tipPlace, setTipPlace] = useState("left");

  const computePlace = () => {
    const el = nodeRef.current;
    if (!el) return "left";
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const space = {
      left: rect.left - edgePadding,
      right: vw - rect.right - edgePadding,
      top: rect.top - edgePadding,
      bottom: vh - rect.bottom - edgePadding,
    };

    const fits = [];
    if (space.left >= tooltipEstimatedSize.width) fits.push("left");
    if (space.right >= tooltipEstimatedSize.width) fits.push("right");
    if (space.top >= tooltipEstimatedSize.height) fits.push("top");
    if (space.bottom >= tooltipEstimatedSize.height) fits.push("bottom");

    if (fits.length) {
      return fits.sort((a, b) => space[b] - space[a])[0];
    }
    return Object.entries(space).sort((a, b) => b[1] - a[1])[0][0];
  };

  useLayoutEffect(() => {
    const el = nodeRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let initialX;
    let initialY;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { x, y } = JSON.parse(saved);
        initialX = clamp(x, edgePadding, vw - rect.width - edgePadding);
        initialY = clamp(y, edgePadding, vh - rect.height - edgePadding);
      } catch {}
    }

    if (initialX == null || initialY == null) {
      initialX = vw - rect.width - defaultRight;
      initialY = vh - rect.height - defaultBottom;
    }

    const x = clamp(initialX, edgePadding, vw - rect.width - edgePadding);
    const y = clamp(initialY, edgePadding, vh - rect.height - edgePadding);
    setPos({ x, y });
  }, [defaultRight, defaultBottom, edgePadding]);

  useEffect(() => {
    const onResize = () => {
      if (!nodeRef.current || pos == null) return;
      const rect = nodeRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setPos({
        x: clamp(pos.x, edgePadding, vw - rect.width - edgePadding),
        y: clamp(pos.y, edgePadding, vh - rect.height - edgePadding),
      });
    };
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [pos, edgePadding]);

  useLayoutEffect(() => {
    if (pos) setTipPlace(computePlace());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos && pos.x, pos && pos.y]);

  useEffect(() => {
    const onResize = () => setTipPlace(computePlace());
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    setMoved(false);
    startRef.current = {
      dx: e.clientX - (pos?.x || 0),
      dy: e.clientY - (pos?.y || 0),
      xAtDown: e.clientX,
      yAtDown: e.clientY,
    };
  };

  const onPointerMove = (e) => {
    if (!dragging || !pos) return;
    const el = nodeRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const nextX = clamp(e.clientX - startRef.current.dx, edgePadding, vw - rect.width - edgePadding);
    const nextY = clamp(e.clientY - startRef.current.dy, edgePadding, vh - rect.height - edgePadding);

    if (!moved) {
      const dist = Math.hypot(e.clientX - startRef.current.xAtDown, e.clientY - startRef.current.yAtDown);
      if (dist > 4) setMoved(true);
    }
    setPos({ x: nextX, y: nextY });
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setDragging(false);
    try {
      if (pos) localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {}
    if (!moved && typeof onActivate === "function") onActivate(e);
  };

  const wrapperStyle = pos == null ? { position: "fixed", right: defaultRight, bottom: defaultBottom } : { position: "fixed", top: 0, left: 0, transform: `translate(${pos.x}px, ${pos.y}px)` };

  return (
    <>
      <div
        ref={nodeRef}
        className={`z-50 touch-none select-none rounded-full transition-shadow duration-150 ${dragging ? " ring-highlightColor/50 bg-white/20 ring-2 backdrop-blur-sm" : ""}`}
        style={wrapperStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}>
        <div
          data-tooltip-id={tooltip ? tooltipId : undefined}
          data-tooltip-content={tooltip || undefined}
          data-tooltip-place={tipPlace}
          className="group relative">
          {children}
        </div>
      </div>

      {tooltip && !dragging && (
        <Tooltip
          id={tooltipId}
          delayShow={tooltipDelay}
          place={tipPlace}
          offset={tooltipOffset}
          positionStrategy="fixed"
          className="!rounded-md !px-3 !py-2 !text-xs !shadow-lg"
          style={{ background: "rgba(0,0,0,0.9)", color: "white", zIndex: 9999 }}
        />
      )}
    </>
  );
}
